// src/shared/modules/ledgers/list/hooks/useLedgerData.ts
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getLedger } from "src/shared/services/servicesv2";
import { ledgerList } from "src/shared/services/interfacev2";

const LEDGER_FETCH_BATCH_SIZE = 500;

const useLedgerData = (
  currentPage: number, 
  rowsPerPage: number, 
  searchTerm: string, 
  showZeroOutstanding: boolean, 
  sortField?: string,
  sortOrder?: string,
) => {
  const [ledgerRecords, setLedgerRecords] = useState<ledgerList[]>([]);
  const [visibleRecords, setVisibleRecords] = useState<ledgerList[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const [ledgerRecordsAll, setLedgerRecordsAll] = useState<ledgerList[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);

  // Debounce search term
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 1000);

    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchTerm]);

  // Main fetch function with sorting parameters
  const fetchLedgerRecords = useCallback((
    pageNumber: number, 
    pageSize: number, 
    search: string, 
    includeSettled?: boolean,
    sortField?: string,
    sortOrder?: string,
  ) => {
    setLoading(true);
    
    // FIX: When there's a search term, ALWAYS set includeSettled to true
    // When no search term, use the toggle value (showZeroOutstanding)
    const shouldIncludeSettled = search.trim() ? true : (includeSettled || false);
    
    getLedger(
      pageNumber, 
      pageSize, 
      search, 
      sortField, 
      sortOrder, 
      shouldIncludeSettled // Pass the corrected value
    )
      .then((response) => {
        const data: ledgerList[] = Array.isArray(response.data.result) ? response.data.result : [];
        setLedgerRecords(data);
        setVisibleRecords(data);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalOutstanding(response.data.total_outstanding_balance || 0);
      })
      .catch((err) => {
        console.error("Error fetching ledger:", err);
      })
      .finally(() => setLoading(false));
  }, []); // Remove showZeroOutstanding from dependencies

  // Fetch records when dependencies change (including sorting)
  useEffect(() => {
    fetchLedgerRecords(
      currentPage + 1,
      rowsPerPage,
      debouncedSearch,
      showZeroOutstanding, // Pass the toggle value
      sortField,
      sortOrder
    );
  }, [currentPage, rowsPerPage, debouncedSearch, showZeroOutstanding, sortField, sortOrder, fetchLedgerRecords]);

  // Function to fetch specific page (for manual refresh)
  const fetchCurrentPageRecords = useCallback(
    async (
      page = currentPage,
      size = rowsPerPage,
      search = debouncedSearch,
      includeSettledOverride?: boolean
    ) => {
      setLoading(true);
      try {
        const shouldIncludeSettled =
          includeSettledOverride ?? (search.trim() ? true : showZeroOutstanding);
        
        const response = await getLedger(
          page + 1, 
          size, 
          search, 
          sortField,
          sortOrder,
          shouldIncludeSettled
        );
        const data = Array.isArray(response.data.result) ? response.data.result : [];
        
        setLedgerRecords(data);
        setVisibleRecords(data);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalOutstanding(response.data.total_outstanding_balance || 0);
        
        return data;
      } catch (err) {
        console.error("Ledger fetch error:", err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [currentPage, rowsPerPage, debouncedSearch, showZeroOutstanding, sortField, sortOrder]
  );

  // Fetch a bounded, server-filtered result set for dropdowns or autocomplete.
  const searchAllLedgers = useCallback(
    async (search = "", pageSize = LEDGER_FETCH_BATCH_SIZE): Promise<ledgerList[]> => {
      try {
        const shouldIncludeSettled = search.trim() ? true : showZeroOutstanding;
        const response = await getLedger(
          1,
          Math.min(Math.max(1, pageSize), LEDGER_FETCH_BATCH_SIZE),
          search,
          sortField,
          sortOrder,
          shouldIncludeSettled
        );
        return Array.isArray(response.data.result) ? response.data.result : [];
      } catch (err) {
        console.error("Error searching ledgers:", err);
        return [];
      }
    },
    [showZeroOutstanding, sortField, sortOrder]
  );

  // Fetch every page only when a feature explicitly requires all ledger records.
  const fetchAllLedgerRecords = useCallback(
    async (chunkSize = LEDGER_FETCH_BATCH_SIZE): Promise<ledgerList[]> => {
      setLoadingAll(true);
      try {
        const allRecords: ledgerList[] = [];
        let pageNumber = 1;
        const safeChunkSize = Math.min(
          Math.max(1, chunkSize),
          LEDGER_FETCH_BATCH_SIZE
        );

        while (true) {
          const response = await getLedger(
            pageNumber,
            safeChunkSize,
            "",
            sortField,
            sortOrder,
            showZeroOutstanding
          );
          const records = Array.isArray(response.data.result) ? response.data.result : [];
          allRecords.push(...records);

          if (records.length < safeChunkSize) break;
          pageNumber += 1;
        }

        setLedgerRecordsAll(allRecords);
        return allRecords;
      } catch (err) {
        console.error("Error fetching all ledgers:", err);
        return [];
      } finally {
        setLoadingAll(false);
      }
    },
    [showZeroOutstanding, sortField, sortOrder]
  );

  // Navigation handler
  const handleRowClick = (id: string) => {
    navigate(`/v1/ledger/${id}`, {
      state: { customers: ledgerRecords },
    });
  };

  // Currency formatting helper
  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined) return "0.00";
    const num = Number(value);
    const thirdDecimal = Math.floor(num * 1000) % 10;
    return thirdDecimal >= 5 ? (Math.ceil(num * 100) / 100).toFixed(2) : num.toFixed(2);
  };

  return {
    ledgerRecords,
    visibleRecords,
    totalRecords,
    totalOutstanding,
    loading,
    handleRowClick,
    formatCurrency,
    fetchCurrentPageRecords,
    showZeroOutstanding,
    ledgerRecordsAll,
    loadingAll,
    fetchAllLedgerRecords,
    searchAllLedgers,
  };
};

export default useLedgerData;
